/**
 * Change / set-password card for the profile section (api-contract.md §2.4).
 *
 * Two modes:
 *  - change (default): asks for the current password; the backend revokes every other session and
 *    returns new tokens, so this device stays signed in (AUTH-04).
 *  - set (passwordSet=false, e.g. an OTP-only patient): no current password; sets the initial
 *    one via /auth/phone/set-password and keeps the session.
 *
 * Reusable across role profile pages. `collapsible` (the account panel) shows just a button until
 * the user wants to change it, with Cancel to fold the fields away again.
 */

import { useState } from "react";

import { changePassword, setInitialPassword } from "../api/auth";
import { ApiError } from "../api/errors";
import { usePasswordRules } from "../api/usePasswordRules";
import { useAuth } from "../auth/AuthContext";
import { passesAll } from "../lib/passwordRules";
import { PasswordChecklist } from "./ui/PasswordChecklist";
import { PasswordInput } from "./ui/PasswordInput";

export function ChangePasswordCard({
  passwordSet = true,
  onPasswordSet,
  collapsible = false,
}: {
  passwordSet?: boolean;
  onPasswordSet?: () => void;
  collapsible?: boolean;
}) {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(!collapsible);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passwordRules = usePasswordRules();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!passesAll(passwordRules, next)) {
      setError("Your new password doesn't meet the rules below yet.");
      return;
    }
    if (next !== confirm) {
      setError("Password and confirmation do not match.");
      return;
    }
    if (passwordSet && next === current) {
      setError("New password must be different from the current one.");
      return;
    }
    setBusy(true);
    try {
      if (passwordSet) {
        const result = await changePassword(current, next);
        if (user && result.access_token && result.refresh_token) {
          // This device carries on with fresh tokens; every other session was signed out (AUTH-04).
          login(user, { access: result.access_token, refresh: result.refresh_token });
          setDone("Password changed. Every other device has been signed out.");
          setCurrent("");
          setNext("");
          setConfirm("");
        } else {
          await logout(); // no session to replace: sign in again
        }
      } else {
        await setInitialPassword(next);
        setDone("Password set.");
        setNext("");
        setConfirm("");
        onPasswordSet?.();
      }
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  const title = passwordSet ? "Change password" : "Set a password";

  const close = () => {
    setOpen(false);
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
  };

  if (!open) {
    return (
      <section className="flex items-center justify-between gap-3 rounded-xl border border-line p-4">
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-ink">Password</span>
          <span className="block text-xs text-ink-muted">
            {done ?? (passwordSet ? "Changing it signs out your other devices." : "Not set yet.")}
          </span>
        </span>
        <button onClick={() => setOpen(true)} className="btn-secondary btn-sm shrink-0">
          {title}
        </button>
      </section>
    );
  }

  return (
    <div className="max-w-lg space-y-4">
      <h2 className={collapsible ? "text-sm font-semibold text-ink" : "text-lg font-semibold text-brand-900"}>{title}</h2>
      <form onSubmit={onSubmit} className="card space-y-4 p-5">
        <p className="text-sm text-ink-muted">
          {passwordSet
            ? "You'll stay signed in here; every other device signed in to your account will be signed out."
            : "Add a password so you can sign in without an OTP next time."}
        </p>
        {error && (
          <div className="rounded-xl border border-danger/20 bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
            {error}
          </div>
        )}
        {done && (
          <div className="rounded-xl border border-success/20 bg-success-soft px-3 py-2 text-sm font-medium text-success">
            {done}
          </div>
        )}
        {passwordSet && (
          <PasswordInput
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Current password"
            className="input"
            required
          />
        )}
        <PasswordInput
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="New password"
          className="input"
          required
        />
        <PasswordInput
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          className="input"
          required
        />
        <PasswordChecklist password={next} rules={passwordRules} />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={busy}
            className="btn-primary"
          >
            {busy ? "Saving…" : title}
          </button>
          {collapsible && (
            <button type="button" onClick={close} disabled={busy} className="btn-secondary">
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "CURRENT_PASSWORD_INCORRECT":
      case "INVALID_CREDENTIALS": // API builds before AUTH-03
        return "Current password is incorrect.";
      case "PASSWORD_NOT_SET":
        return "This account doesn't have a password yet. Reload the page to set one.";
      // PASSWORD_WEAK: the server's message states the rule it applies (AUTH-15).
      case "PASSWORD_ALREADY_SET":
        return "A password is already set. Use change password instead.";
      default:
        return err.message;
    }
  }
  return "Something went wrong. Please try again.";
}
